import React, { useEffect, useMemo, useState } from 'react';
import { useGasI18n } from '../../shared/kit';
import { ActivityCard } from '../user-home/components/ActivityCard';
import { fetchArchiveYear, fetchCurrent } from './api';
import { agendaItemToCard, agendaOnlineItemToCard } from './cardAdapter';
import { AgendaCompactView } from './components/AgendaCompactView';
import { AgendaCalendarView } from './components/AgendaCalendarView';
import { AgendaViewSwitcher } from './components/AgendaViewSwitcher';
import type { AgendaInitData, AgendaItem, AgendaReadModel, AgendaView, AgendaYear } from './types';

const SUPPORTED_VIEWS: AgendaView[] = ['grid', 'compact', 'month', 'calendar'];

interface Props {
    initData: AgendaInitData;
}

function flattenYear(year: AgendaYear | undefined): AgendaItem[] {
    if (!year) {
        return [];
    }
    return Object.values(year.months).flatMap((m) => m.items);
}

function matchFilters(item: AgendaItem, type: string, text: string): boolean {
    const matchType = !type || item.type === type;
    const matchText = !text || item.title.toLowerCase().includes(text);
    return matchType && matchText;
}

export default function App({ initData }: Props): React.ReactElement {
    const { t } = useGasI18n();
    const { rest_base_url: restBaseUrl, locale } = initData;

    const [model, setModel] = useState<AgendaReadModel | null>(initData.initial ?? null);
    const [loading, setLoading] = useState(!initData.initial);
    const [error, setError] = useState(false);

    const currentYear = model?.current_year ?? new Date().getFullYear();
    const yearMinus1 = currentYear - 1;

    const [activeTab, setActiveTab] = useState<string>(String(currentYear));
    const [filterType, setFilterType] = useState('');
    const [filterText, setFilterText] = useState('');

    // Modes d'affichage (Lot 3) : intersection config × vues supportées.
    const availableViews = useMemo<AgendaView[]>(() => {
        const configured = (initData.display?.available_views ?? ['grid']).filter((v) => SUPPORTED_VIEWS.includes(v));
        return configured.length > 0 ? configured : ['grid'];
    }, [initData.display]);
    const showPast = initData.display?.show_past !== false;
    const [view, setView] = useState<AgendaView>(() => {
        const def = initData.display?.default_view ?? 'grid';
        return availableViews.includes(def) ? def : availableViews[0];
    });

    // Archives chargées à la demande (scope=years=YYYY) → cache local.
    const [archiveYears, setArchiveYears] = useState<Record<string, AgendaItem[]>>({});
    const [archiveSelected, setArchiveSelected] = useState('');
    const [archiveLoading, setArchiveLoading] = useState(false);

    useEffect(() => {
        if (initData.initial) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        fetchCurrent(restBaseUrl, locale)
            .then((data) => {
                if (!cancelled) {
                    setModel(data);
                    setActiveTab(String(data.current_year));
                }
            })
            .catch(() => !cancelled && setError(true))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [initData.initial, restBaseUrl, locale]);

    const archiveYearList = useMemo(() => {
        if (!model) {
            return [] as Array<{ year: string; count: number }>;
        }
        return Object.entries(model.year_counts)
            .map(([year, count]) => ({ year, count }))
            .filter((e) => Number(e.year) < yearMinus1)
            .sort((a, b) => Number(b.year) - Number(a.year));
    }, [model, yearMinus1]);

    function handleSwitchTab(tab: string): void {
        setActiveTab(tab);
        setFilterType('');
        setFilterText('');
    }

    async function handleArchiveSelect(year: string): Promise<void> {
        setArchiveSelected(year);
        if (!year || archiveYears[year]) {
            return;
        }
        setArchiveLoading(true);
        try {
            const data = await fetchArchiveYear(restBaseUrl, locale, year);
            setArchiveYears((prev) => ({ ...prev, [year]: flattenYear(data.years[year]) }));
        } catch {
            setArchiveYears((prev) => ({ ...prev, [year]: [] }));
        } finally {
            setArchiveLoading(false);
        }
    }

    if (loading) {
        return <div className="gas-agenda__loading">{t('common.loading', {}, 'Chargement…')}</div>;
    }
    if (error || !model) {
        return <div className="gas-agenda__empty"><p>{t('errors.generic', {}, 'Une erreur est survenue.')}</p></div>;
    }

    const textLc = filterText.toLowerCase().trim();
    const renderItems = (items: AgendaItem[]): React.ReactElement => {
        const filtered = items.filter((it) => matchFilters(it, filterType, textLc));
        if (filtered.length === 0) {
            return (
                <div className="gas-agenda__empty gas-agenda__empty--filtered">
                    <p>{t('agenda.empty_filtered', {}, 'Aucune activité ne correspond à vos critères.')}</p>
                </div>
            );
        }
        if (view === 'compact') {
            return <AgendaCompactView items={filtered} locale={locale} t={t} />;
        }
        if (view === 'month') {
            return <AgendaCompactView items={filtered} groupByMonth locale={locale} t={t} />;
        }
        if (view === 'calendar') {
            return <AgendaCalendarView items={filtered} t={t} />;
        }
        return (
            <div className="gas-agenda__grid">
                {filtered.map((it) => (
                    <div
                        key={it.id}
                        className="gas-agenda__card-wrapper"
                        data-type={it.type}
                        data-title={it.title.toLowerCase()}
                    >
                        <ActivityCard card={agendaItemToCard(it, t)} />
                    </div>
                ))}
            </div>
        );
    };
    // grille/liste : on garde les sections « à venir / passées » ;
    // mois/calendrier : rendu combiné (le regroupement temporel est porté par la vue).
    const splitSections = view === 'grid' || view === 'compact';

    const upcoming = model.upcoming ?? [];
    const currentYearItems = flattenYear(model.years[String(currentYear)]);
    const upcomingIds = new Set(upcoming.map((i) => i.id));
    const pastCurrentYear = currentYearItems.filter((i) => !upcomingIds.has(i.id));
    const yearMinus1Items = flattenYear(model.years[String(yearMinus1)]);
    const online = model.online ?? [];

    const tabCount = (n: number): number => model.year_counts[String(n)] ?? 0;
    const archivesTotal = archiveYearList.reduce((sum, e) => sum + e.count, 0);

    return (
        <div className="gas-agenda">
            {/* Hero */}
            <section className="gas-agenda__hero">
                {initData.is_user_member ? (
                    <div className="gas-agenda__hero-layout--member">
                        <div className="gas-agenda__hero-left">
                            <h1>{t('agenda.title', {}, 'Agenda des activités')}</h1>
                            <p>{t('agenda.intro_member', { name: initData.association_name }, `Formations, ateliers et événements de ${initData.association_name}.`)}</p>
                        </div>
                        {initData.webcal_url && (
                            <div className="gas-agenda__hero-right">
                                <div className="gas-agenda__calendar-box">
                                    <h4>{t('agenda.calendar.title', {}, 'Calendrier')}</h4>
                                    <a href={initData.webcal_url} className="gas-button gas-button--outline gas-button--small">
                                        {t('agenda.calendar.subscribe', {}, 'S’abonner')}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="gas-agenda__hero-layout--visitor">
                        <h1>{t('agenda.title', {}, 'Agenda des activités')}</h1>
                        <p>{t('agenda.intro_visitor', {}, 'Découvrez toutes nos formations, ateliers et événements.')}</p>
                    </div>
                )}
            </section>

            {/* Tabs */}
            <nav className="gas-agenda__tabs" role="tablist">
                {(showPast ? [currentYear, yearMinus1] : [currentYear]).map((year) => (
                    <button
                        key={year}
                        type="button"
                        role="tab"
                        className={`gas-agenda__tab${activeTab === String(year) ? ' gas-agenda__tab--active' : ''}`}
                        aria-selected={activeTab === String(year)}
                        onClick={() => handleSwitchTab(String(year))}
                    >
                        {year}
                        <span className="gas-agenda__tab-count">{tabCount(year)}</span>
                    </button>
                ))}
                {showPast && archiveYearList.length > 0 && (
                    <button
                        type="button"
                        role="tab"
                        className={`gas-agenda__tab${activeTab === 'archives' ? ' gas-agenda__tab--active' : ''}`}
                        aria-selected={activeTab === 'archives'}
                        onClick={() => handleSwitchTab('archives')}
                    >
                        {t('agenda.tab.archives', {}, 'Archives')}
                        <span className="gas-agenda__tab-count">{archivesTotal}</span>
                    </button>
                )}
                {online.length > 0 && (
                    <button
                        type="button"
                        role="tab"
                        className={`gas-agenda__tab${activeTab === 'online' ? ' gas-agenda__tab--active' : ''}`}
                        aria-selected={activeTab === 'online'}
                        onClick={() => handleSwitchTab('online')}
                    >
                        {t('agenda.tab.online', {}, 'En ligne')}
                        <span className="gas-agenda__tab-count">{online.length}</span>
                    </button>
                )}
            </nav>

            {/* Filtres (masqués sur l'onglet "en ligne") */}
            {activeTab !== 'online' && (
                <div className="gas-agenda__filters">
                    <div className="gas-agenda__filter-group">
                        <label htmlFor="gas-agenda-filter-type">{t('agenda.filter.type', {}, 'Type')}</label>
                        <select
                            id="gas-agenda-filter-type"
                            className="gas-agenda__filter-type"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="">{t('agenda.filter.all', {}, 'Tous')}</option>
                            {Object.entries(model.types).map(([slug, label]) => (
                                <option key={slug} value={slug}>{label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="gas-agenda__filter-group">
                        <label htmlFor="gas-agenda-filter-search">{t('common.search', {}, 'Recherche')}</label>
                        <input
                            id="gas-agenda-filter-search"
                            type="text"
                            className="gas-agenda__filter-search"
                            value={filterText}
                            placeholder={t('agenda.filter.search_placeholder', {}, 'Rechercher une activité…')}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </div>
                    <AgendaViewSwitcher views={availableViews} active={view} onChange={setView} t={t} />
                </div>
            )}

            {/* Panel année courante */}
            {activeTab === String(currentYear) && (
                <div className="gas-agenda__panel" data-panel={currentYear}>
                    {splitSections ? (
                        <>
                            {upcoming.length > 0 && (
                                <>
                                    <h3 className="gas-agenda__section-title">{t('agenda.section.upcoming', {}, 'Activités à venir')}</h3>
                                    {renderItems(upcoming)}
                                </>
                            )}
                            {showPast && pastCurrentYear.length > 0 && (
                                <>
                                    <h3 className="gas-agenda__section-title">{t('agenda.section.past_year', { year: currentYear }, `Activités passées ${currentYear}`)}</h3>
                                    {renderItems(pastCurrentYear)}
                                </>
                            )}
                        </>
                    ) : (
                        renderItems(showPast ? currentYearItems : upcoming)
                    )}
                </div>
            )}

            {/* Panel année-1 */}
            {showPast && activeTab === String(yearMinus1) && (
                <div className="gas-agenda__panel" data-panel={yearMinus1}>
                    {renderItems(yearMinus1Items)}
                </div>
            )}

            {/* Panel archives */}
            {activeTab === 'archives' && (
                <div className="gas-agenda__panel" data-panel="archives">
                    <div className="gas-agenda__archives-controls">
                        <label htmlFor="gas-agenda-archive-year">{t('agenda.archives.choose_year', {}, 'Choisir une année')}</label>
                        <select
                            id="gas-agenda-archive-year"
                            className="gas-agenda__archives-select"
                            value={archiveSelected}
                            onChange={(e) => handleArchiveSelect(e.target.value)}
                        >
                            <option value="">{t('agenda.archives.select_prompt', {}, '— Sélectionnez —')}</option>
                            {archiveYearList.map((e) => (
                                <option key={e.year} value={e.year}>{`${e.year} (${e.count})`}</option>
                            ))}
                        </select>
                    </div>
                    {archiveLoading && (
                        <div className="gas-agenda__archives-loading">{t('common.loading', {}, 'Chargement…')}</div>
                    )}
                    {!archiveLoading && archiveSelected && (
                        <div className="gas-agenda__archives-content">
                            {renderItems(archiveYears[archiveSelected] ?? [])}
                        </div>
                    )}
                </div>
            )}

            {/* Panel en ligne */}
            {activeTab === 'online' && (
                <div className="gas-agenda__panel" data-panel="online">
                    <div className="gas-agenda__grid">
                        {online.map((it) => (
                            <div key={it.id} className="gas-agenda__card-wrapper" data-type={it.type}>
                                <ActivityCard card={agendaOnlineItemToCard(it, t)} />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* CTA contextuel */}
            {!initData.is_user_member && initData.become_member_url && (
                <div className="gas-agenda__cta">
                    <div className="gas-agenda__cta-content">
                        <div className="gas-agenda__cta-text">
                            <h3>{t('agenda.cta.join_title', {}, 'Rejoignez notre communauté')}</h3>
                        </div>
                        <div className="gas-agenda__cta-action">
                            <a href={initData.become_member_url} className="gas-button gas-button--primary">
                                {t('agenda.cta.become_member', {}, 'Devenir membre')}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
